import { useCallback, useEffect, useState } from 'react';
import { FileText, ExternalLink, Folder } from 'lucide-react';
import Panel from './Panel.jsx';
import DocumentListRows from './DocumentListRows.jsx';
import { getAccountDocumentList, groupByPolicy } from '@/services/document-list.service.js';

// SCREEN 6, THE ACCOUNT'S DOCUMENTS (paperwork-1 D8, PLAN section 3; L1b-B3). One line at the top says what it is for; the
// documents home's rows follow, by derived name, grouped by policy, each with Open (the logged download door). The
// WorkDrive tree stays BENEATH, labelled "WorkDrive (old files)", until the import is applied and its second dry run reads 0
// (after-merge list); it is the same tree as before.
// `documents` is the WorkDrive tree's shape:
//   { years: [{ year, deals: [{ app_number, workdrive_url, files: [...] }],
//               loose_files: [] }],
//     total_files, account_folder_urls, no_account_number, no_folder_found }
// `bare` renders the tree alone without the Panel chrome (the Paperwork tab, which replaced DocsTab in paperwork-1 L2-1, wraps it in its own "All files" panel; unchanged).
export default function DocumentsPanel({ documents, accountId = null, bare = false }) {
  const years = documents?.years || [];
  const treeEmpty =
    years.length === 0
      ? documents?.no_folder_found
        ? 'No WorkDrive folder for this account.'
        : 'No files in WorkDrive.'
      : null;

  if (bare) {
    if (!documents) return null;
    if (treeEmpty) return <p className="text-xs italic text-text-dim">{treeEmpty}</p>;
    return <DocsTree years={years} />;
  }

  return (
    <Panel title="Documents">
      <p className="mb-3 text-xs text-text-muted">Every document filed on this account, by policy, under the name TRACON gives it.</p>
      <AccountDocumentList accountId={accountId} />
      {documents ? (
        <div className="mt-4 border-t border-border pt-3" data-workdrive-old-files>
          <div className="mb-2 flex items-baseline justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">
            <span>WorkDrive (old files)</span>
            <span className="font-mono">{documents.total_files || 0}</span>
          </div>
          {treeEmpty ? <p className="text-xs italic text-text-dim">{treeEmpty}</p> : <DocsTree years={years} />}
        </div>
      ) : null}
    </Panel>
  );
}

function AccountDocumentList({ accountId }) {
  const [state, setState] = useState({ loading: true, rows: [], error: null });
  const load = useCallback(async () => {
    if (!accountId) { setState({ loading: false, rows: [], error: 'This page does not know the account yet.' }); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await getAccountDocumentList(accountId);
      setState({ loading: false, rows: r?.documents || [], error: null });
    } catch (e) {
      setState({ loading: false, rows: [], error: e?.body?.error || e?.message || 'the server did not answer' });
    }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  if (state.loading) return <p className="text-xs text-text-dim">Reading the documents...</p>;
  if (state.error) {
    return (
      <p className="flex flex-wrap items-center gap-2 text-xs text-warning" data-document-list-error>
        <span>Could not read this account&apos;s documents: {state.error}</span>
        <button type="button" onClick={load} className="rounded-md border border-border-strong px-2 py-0.5 text-[11px] text-text hover:bg-surface-strong">Read the documents again</button>
      </p>
    );
  }
  if (state.rows.length === 0) return <p className="text-xs italic text-text-dim">No documents filed on this account yet.</p>;
  return (
    <div className="space-y-3" data-account-documents>
      {groupByPolicy(state.rows).map((g) => (
        <div key={g.key || 'account'} data-document-policy={g.key || 'account'}>
          <div className="mb-1 flex items-baseline justify-between text-[11px] font-semibold text-text-muted">
            <span>{g.label}</span>
            <span className="font-mono text-[10px] text-text-dim">{g.rows.length}</span>
          </div>
          <DocumentListRows rows={g.rows} />
        </div>
      ))}
    </div>
  );
}

function DocsTree({ years }) {
  return (
    <div className="space-y-3">
        {years.map((y) => (
          <details key={y.year} open={years.length === 1}>
            <summary className="cursor-pointer list-none">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-semibold uppercase tracking-wide text-text-muted">
                  {y.year}
                </span>
                <span className="font-mono text-[10px] text-text-dim">
                  {y.deals.reduce((n, d) => n + d.files.length, 0)} files
                </span>
              </div>
            </summary>
            <ul className="mt-2 space-y-1">
              {y.deals.map((d) => (
                <li key={d.app_number} className="rounded-card bg-bg/40 p-2">
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="font-mono text-[11px] text-text-muted">
                      {d.app_number}
                    </span>
                    {d.workdrive_url ? (
                      <a
                        href={d.workdrive_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-[10px] text-info hover:text-accent"
                      >
                        Open <ExternalLink size={11} strokeWidth={1.5} />
                      </a>
                    ) : null}
                  </div>
                  <ul className="space-y-0.5">
                    {d.files.map((f) => (
                      <li key={f.id} className="flex items-center gap-1.5 text-xs">
                        {f.type === 'folder' ? (
                          <Folder size={12} strokeWidth={1.5} className="text-text-dim" />
                        ) : (
                          <FileText size={12} strokeWidth={1.5} className="text-text-dim" />
                        )}
                        <a
                          href={f.workdrive_url}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-text-muted hover:text-accent"
                        >
                          {f.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
  );
}
